import { checkDirectoryIsValid, DirectoryError } from "@/storage/open-directory"
import { ProjectItem } from "./projects-service"
import { ensureSampleProjectMounted } from "./sample-project-loader"
import { resolveSampleProjectPath } from "./sample-project-path"

const PREFLIGHT_VALIDITY_TTL_MS = 15_000

type ValidatedEntry = {
	handle: FileSystemDirectoryHandle
	validatedAtMs: number
}

const recentlyValidatedProjects = new Map<string, ValidatedEntry>()

export type ProjectOpenPreflightResult =
	| {
			ok: true
			handle: FileSystemDirectoryHandle
	  }
	| {
			ok: false
			reason: DirectoryError | "sample_mount_error"
			error?: unknown
	  }

export async function preflightProjectOpen(
	project: ProjectItem,
): Promise<ProjectOpenPreflightResult> {
	let handle = project.handle

	if (project.isSampleProject) {
		try {
			const rootHandle = await ensureSampleProjectMounted(project.id)
			handle = await resolveSampleProjectPath(rootHandle, project)
			project.handle = handle
		} catch (error) {
			return {
				ok: false,
				reason: "sample_mount_error",
				error,
			}
		}
	}

	const validatedHandle = await checkDirectoryIsValid(handle, ["no_handle", "no_directory"])

	if (!(validatedHandle instanceof FileSystemDirectoryHandle)) {
		return {
			ok: false,
			reason: validatedHandle,
		}
	}

	project.handle = validatedHandle
	recentlyValidatedProjects.set(project.id, {
		handle: validatedHandle,
		validatedAtMs: Date.now(),
	})

	return {
		ok: true,
		handle: validatedHandle,
	}
}

export async function consumeRecentProjectPreflightHandle(
	projectId: string,
	handle: FileSystemDirectoryHandle | undefined,
) {
	const recentValidation = recentlyValidatedProjects.get(projectId)
	if (!recentValidation) {
		return null
	}

	if (Date.now() - recentValidation.validatedAtMs > PREFLIGHT_VALIDITY_TTL_MS) {
		recentlyValidatedProjects.delete(projectId)
		return null
	}

	if (!handle) {
		recentlyValidatedProjects.delete(projectId)
		return null
	}

	try {
		const isSameEntry = await handle.isSameEntry(recentValidation.handle)
		if (!isSameEntry) {
			recentlyValidatedProjects.delete(projectId)
			return null
		}
	} catch {
		recentlyValidatedProjects.delete(projectId)
		return null
	}

	recentlyValidatedProjects.delete(projectId)
	return recentValidation.handle
}
